import { history } from 'ui/customHooks/useHistory'
import baseAnalyticsEvent, {
  analyticsConfig
} from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import { PAGE_EVENTS, setPageAnalytics } from 'const/analytics/defaults'
import { userAccountInfo } from 'helpers/user'
import { EventOptions } from 'syf-js-utilities/typings/analytics'
import config from 'const/config'
import routePaths from 'const/routePaths'
import { UserAccountType } from 'typings/userInfo'
import { CardType } from 'typings/account'

/**
 * Temporary method to handle new custom event for Tealium/utag.js. This should
 * be removed and replaced by the triggerAnalyticsEvent from latest sfy-js-util
 * after upgrade the version in dependencies. (may have breaking changes)
 */
const analyticsEventWithPageView = baseAnalyticsEvent

const { CLIENT_ID } = config

analyticsConfig.defaults.Country_Language = `us|en`
analyticsConfig.defaults.SiteIdentifier = 'eserviceresponsive'
analyticsConfig.defaults.PNL = 'rc'
analyticsConfig.defaults.ClientName = CLIENT_ID
analyticsConfig.defaults.CustomerType = 'commercial'
analyticsConfig.defaults.Message = 'commercial_pageload'
analyticsConfig.defaults.Product = 'generic'

// Refactor the repetitive code below to iterate over array of routePaths keys
analyticsConfig.pageEvents[''] = PAGE_EVENTS[routePaths.home]
analyticsConfig.pageEvents[routePaths.home] = PAGE_EVENTS[routePaths.home]
analyticsConfig.pageEvents[routePaths.summary] = PAGE_EVENTS[routePaths.summary]
analyticsConfig.pageEvents[routePaths.makePayment] =
  PAGE_EVENTS[routePaths.makePayment]
analyticsConfig.pageEvents[routePaths.viewPayment] =
  PAGE_EVENTS[routePaths.viewPayment]
analyticsConfig.pageEvents[routePaths.invoice] = PAGE_EVENTS[routePaths.invoice]
analyticsConfig.pageEvents[routePaths.statement] =
  PAGE_EVENTS[routePaths.statement]
analyticsConfig.pageEvents[routePaths.transactions] =
  PAGE_EVENTS[routePaths.transactions]
analyticsConfig.pageEvents[routePaths.credit] = PAGE_EVENTS[routePaths.credit]
analyticsConfig.pageEvents[routePaths.companyProfile] =
  PAGE_EVENTS[routePaths.companyProfile]
analyticsConfig.pageEvents[routePaths.managePayment] =
  PAGE_EVENTS[routePaths.managePayment]
analyticsConfig.pageEvents[routePaths.manageBuyers] =
  PAGE_EVENTS[routePaths.manageBuyers]
analyticsConfig.pageEvents[routePaths.manageUser] =
  PAGE_EVENTS[routePaths.manageUser]
analyticsConfig.pageEvents[routePaths.manageLinkedAccounts] =
  PAGE_EVENTS[routePaths.manageLinkedAccounts]
analyticsConfig.pageEvents[routePaths.manageChildAccount] =
  PAGE_EVENTS[routePaths.manageChildAccount]
analyticsConfig.pageEvents[routePaths.emailStatement] =
  PAGE_EVENTS[routePaths.emailStatement]
analyticsConfig.pageEvents[routePaths.increaseCreditLine] =
  PAGE_EVENTS[routePaths.increaseCreditLine]
analyticsConfig.pageEvents[routePaths.taxForms] =
  PAGE_EVENTS[routePaths.taxForms]
analyticsConfig.pageEvents[routePaths.editJobName] =
  PAGE_EVENTS[routePaths.editJobName]
analyticsConfig.pageEvents[routePaths.closeAccount] =
  PAGE_EVENTS[routePaths.closeAccount]
analyticsConfig.pageEvents[routePaths.userGuide] =
  PAGE_EVENTS[routePaths.userGuide]
analyticsConfig.pageEvents[routePaths.contactUs] =
  PAGE_EVENTS[routePaths.contactUs]
analyticsConfig.pageEvents[routePaths.recoverUserId] =
  PAGE_EVENTS[routePaths.recoverUserId]
analyticsConfig.pageEvents[routePaths.resetPassword] =
  PAGE_EVENTS[routePaths.resetPassword]
analyticsConfig.pageEvents[routePaths.invoiceBillPay] =
  PAGE_EVENTS[routePaths.invoiceBillPay]
analyticsConfig.pageEvents[routePaths.ibpStatus] =
  PAGE_EVENTS[routePaths.ibpStatus]
analyticsConfig.pageEvents[routePaths.videoGallery] =
  PAGE_EVENTS[routePaths.videoGallery]
analyticsConfig.pageEvents[routePaths.editBusinessInfo] =
  PAGE_EVENTS[routePaths.editBusinessInfo]
analyticsConfig.pageEvents[routePaths.editUser] =
  PAGE_EVENTS[routePaths.editUser]
analyticsConfig.pageEvents[routePaths.registration] =
  PAGE_EVENTS[routePaths.registration]
analyticsConfig.pageEvents[routePaths.registerChildAccount] =
  PAGE_EVENTS[routePaths.registerChildAccount]
analyticsConfig.pageEvents[routePaths.error] = PAGE_EVENTS[routePaths.error]
analyticsConfig.pageEvents[routePaths.logout] = PAGE_EVENTS[routePaths.logout]
analyticsConfig.pageEvents[routePaths.requestBuyerCards] =
  PAGE_EVENTS[routePaths.requestBuyerCards]
analyticsConfig.pageEvents[routePaths.lostCard] =
  PAGE_EVENTS[routePaths.lostCard]

history.listen((location: { pathname: string | number }) => {
  const { isProx, cardType, analyticsProductType } = userAccountInfo
  const isCardActivationPath = location.pathname
    ?.toString()
    .startsWith(routePaths.activation)
  analyticsConfig.defaults.Product = analyticsProductType
  /* Product will be set to generic when the user logout */
  const isInitialPathName = location.pathname === '/'

  // card activation page view analytics events are handled within the components
  if (isCardActivationPath) {
    return
  }

  if (isInitialPathName) {
    userAccountInfo.analyticsProductType = 'generic'
    userAccountInfo.cardType = ''
    analyticsConfig.defaults.Product = 'generic'
  }

  if (!isProx) {
    setPageAnalytics(location.pathname)
  }

  analyticsEventWithPageView({
    pathName: `${location.pathname}`,
    cardtype: isInitialPathName ? '' : cardType
  })
})

export interface PageEventType {
  subFunction?: string
  message?: string
  pathName?: string
  pageName?: string
  errorMessage?: string
  pageKind?: string
  pageFunction?: string
  name?: string
  isUpdated?: boolean
  pageSubFunction?: string
  cardtype?: CardType
}

const analyticsWithPathName = (eventOptions: PageEventType): void => {
  const { name, subFunction, pageFunction, message, ...rest } = eventOptions

  const props: PageEventType = {
    pageFunction
  }

  if (name) {
    props.pageName = name
  }

  if (subFunction) {
    props.pageSubFunction = subFunction
  }

  if (message) {
    props.message = message
  }

  analyticsEventWithPageView({
    ...props,
    pathName: history.location.pathname,
    ...rest
  } as EventOptions)
}

export const setProductType = (productType: UserAccountType) => {
  analyticsConfig.defaults.Product = productType
}

export const triggerAnalytics = (
  name = '',
  subFunction = '',
  pageFunction = ''
): void => {
  const eventOptions: EventOptions = {
    pageName: name,
    pageSubFunction: subFunction,
    pathName: history.location.pathname
  }

  if (pageFunction) {
    eventOptions.pageFunction = pageFunction
  }

  analyticsEventWithPageView(eventOptions)
}

export const setPageKind = (path: string, source: string): void => {
  PAGE_EVENTS[path].PAGE_KIND = source
}

export const triggerAnalyticsEvent = analyticsWithPathName

/**
 * When adding an event, the following attributes should be assigned to the subject element:
 *  'data-analytics': AnalyticsEvent
 *  'data-reason'?: string
 *  'data-object'?: string
 *  'data-type'?: string
 */
export enum AnalyticsEvent {
  click = 'clicktrackingevent',
  dropdown = 'clicktrackingeventDropdown'
}

/**
 * Method returns the name used by analytics to track the profile creation process.
 *
 * @param withAuthCode indicates if user is registering using an auth code
 * @param isPartialRegistration indicates user partially registered without an auth code
 * @returns string with page function for analytics on profile creation process
 */
export const getAccountCreationPageFunction = (
  withAuthCode: boolean,
  isPartialRegistration: boolean
) => {
  if (isPartialRegistration) {
    return 'partial profile creation without auth code'
  }
  if (withAuthCode) {
    return 'profile creation with auth code'
  }
  return 'profile creation without auth code'
}
